import { useRoleContext } from "./useRoleContext";
import { useSwrFetch } from "./useSwrFetch";

import type {
  EmbeddedDashboardName,
  HasEmbeddedAnalyticsAccessResponse,
} from "components/EmbeddedAnalytics/types";

interface UseEmbeddedAnalyticsAccessProps {
  dashboard: EmbeddedDashboardName;
}

/**
 * Whether the signed-in shipper can use one embedded analytics dashboard.
 *
 * `hasAccess` is null until the request resolves. The backend answers false
 * both for companies without the entitlement and while that dashboard is still
 * being built, so the app never offers a dashboard it cannot serve. Asked per
 * dashboard: one still being built must not gate one that is ready.
 */
export function useEmbeddedAnalyticsAccess(
  props: UseEmbeddedAnalyticsAccessProps,
) {
  const { dashboard } = props;
  const { role } = useRoleContext();

  const url =
    role?.type === "SHIPPER"
      ? `/shippers/${role.id}/viz/embedded/has-access?dashboard=${dashboard}`
      : null;

  const { data, loading, error } =
    useSwrFetch<HasEmbeddedAnalyticsAccessResponse>({
      url,
      errorMessage: "Failed to fetch analytics access details",
    });

  return {
    hasAccess: data?.has_access ?? null,
    loading,
    error,
  };
}
